import type { SxProps, Theme } from '@mui/material';
import { Color } from 'shared/utils/colors';

/** Matches MUI OutlinedInput inset — same on left and right of the field. */
const RICH_TEXT_CONTENT_PADDING_PX = 14;
/** Compact empty/single-line field height. */
const COMPACT_FIELD_HEIGHT_PX = 56;
/** Line height of the comment text (matches the compact single-line state). */
const RICH_TEXT_LINE_HEIGHT_PX = 24;
/** Top/bottom inset that centres one text line in the compact-height field. */
const RICH_TEXT_CONTENT_VERTICAL_PADDING_PX =
  (COMPACT_FIELD_HEIGHT_PX - RICH_TEXT_LINE_HEIGHT_PX) / 2;
/** Original toolbar row: padding 8×2 + small IconButton + 1px border. */
const RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX = 48;
const FORMAT_TOGGLE_BUTTON_PX = 44;
/** Padding of a small MUI IconButton around its glyph. */
const TOOLBAR_ICON_BUTTON_INSET_PX = 5;
const FORMAT_TOOLBAR_SLIDE_TRANSITION = (theme: Theme) =>
  theme.transitions.create('transform', {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  });

const FORMAT_TOOLBAR_CONTENT_SLIDE_TRANSITION = (theme: Theme) =>
  theme.transitions.create(['height', 'padding-top', 'padding-right'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  });

const FORMAT_TOOLBAR_LAYOUT_TRANSITION = (theme: Theme) =>
  theme.transitions.create(['height', 'padding-top', 'padding-right'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  });

export const commentRootSx: SxProps<Theme> = {
  position: 'relative',
};

export const floatingLabelSx = (
  shrunk: boolean,
  focused: boolean
): SxProps<Theme> => ({
  position: 'absolute',
  left: 0,
  top: 0,
  transform: shrunk
    ? 'translate(14px, -9px) scale(0.75)'
    : 'translate(14px, 16px) scale(1)',
  transformOrigin: 'top left',
  px: 0.5,
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.4375,
  letterSpacing: '0.00938em',
  bgcolor: 'background.paper',
  maxWidth: 'calc(100% - 28px)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  zIndex: 10,
  pointerEvents: 'none',
  color: focused ? Color.primaryOrangeDark : 'text.secondary',
  transition: (theme) =>
    theme.transitions.create(['color', 'transform', 'max-width'], {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.easeOut,
    }),
});

export const editorHostSx = {
  position: 'relative',
  '& label': {
    display: 'none',
  },
  '& [data-rich-text-wrapper="true"]': {
    position: 'relative',
    cursor: 'text',
    overflow: 'hidden',
  },
  '& [data-rich-text-content="true"]': {
    boxSizing: 'border-box',
    // Vertical inset centres a single line in the field; the caret still
    // starts at the top-left and longer text grows the field downwards.
    padding: `${RICH_TEXT_CONTENT_VERTICAL_PADDING_PX}px ${RICH_TEXT_CONTENT_PADDING_PX}px`,
    height: 'auto',
    // Same height as the compact empty field, so the first typed character
    // does not shift the field or the "Tт" toggle.
    minHeight: `${COMPACT_FIELD_HEIGHT_PX}px`,
    maxHeight: 'none',
  },
  '& .ProseMirror': {
    height: 'auto',
    minHeight: '1.5em',
    maxHeight: 'none',
    overflow: 'visible',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // More specific than the editor's own `.ProseMirror` min-height (minRows).
  '& [data-rich-text-content="true"] .ProseMirror': {
    minHeight: `${RICH_TEXT_LINE_HEIGHT_PX}px`,
  },
  '& .ProseMirror p': {
    lineHeight: `${RICH_TEXT_LINE_HEIGHT_PX}px`,
  },
  // The editor's paragraph spacing would otherwise leave a gap under the last
  // line and push a single line above the middle.
  '& .ProseMirror > :last-child': {
    marginBottom: 0,
  },
  '& .ProseMirror p.is-editor-empty:first-child::before': {
    color: 'text.disabled',
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
  '& [data-rich-text-toolbar="true"]': {
    // First icon glyph lines up with the text inset (small IconButton adds 5px).
    paddingLeft: `${RICH_TEXT_CONTENT_PADDING_PX - TOOLBAR_ICON_BUTTON_INSET_PX}px`,
    paddingRight: `${RICH_TEXT_CONTENT_PADDING_PX - TOOLBAR_ICON_BUTTON_INSET_PX}px`,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    transform: 'translateY(-100%)',
    transition: FORMAT_TOOLBAR_SLIDE_TRANSITION,
    pointerEvents: 'none',
  },
  '&[data-format-toolbar-open="true"] [data-rich-text-toolbar="true"]': {
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
  // Text keeps the same inset below the toolbar as on the other sides.
  '&[data-format-toolbar-open="true"] [data-rich-text-content="true"]': {
    paddingTop: `${RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX + RICH_TEXT_CONTENT_VERTICAL_PADDING_PX}px`,
    paddingBottom: `${RICH_TEXT_CONTENT_VERTICAL_PADDING_PX}px`,
    transition: FORMAT_TOOLBAR_CONTENT_SLIDE_TRANSITION,
  },
  '&:not([data-format-toolbar-open="true"]) [data-rich-text-content="true"]': {
    paddingRight: `${RICH_TEXT_CONTENT_PADDING_PX + FORMAT_TOGGLE_BUTTON_PX}px`,
    transition: FORMAT_TOOLBAR_CONTENT_SLIDE_TRANSITION,
  },
  '& [data-rich-text-helper="true"]': {
    maxHeight: 0,
    opacity: 0,
    mt: 0,
    mb: 0,
    py: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    transition: (theme) =>
      theme.transitions.create(['max-height', 'opacity'], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
  },
  '&[data-format-toolbar-open="true"] [data-rich-text-helper="true"]': {
    maxHeight: 24,
    opacity: 1,
    pointerEvents: 'auto',
  },
} satisfies SxProps<Theme>;

export const formatToolbarToggleSlotSx = (
  toolbarOpen: boolean
): SxProps<Theme> => ({
  position: 'absolute',
  top: 0,
  right: `${RICH_TEXT_CONTENT_PADDING_PX}px`,
  zIndex: 2,
  height: toolbarOpen
    ? RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX
    : COMPACT_FIELD_HEIGHT_PX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: (theme) =>
    theme.transitions.create('height', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
});

export const formatToolbarToggleSx: SxProps<Theme> = {
  width: FORMAT_TOGGLE_BUTTON_PX,
  height: 30,
  p: 0.5,
  borderRadius: '4px !important',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.25,
  color: 'action.active',
  bgcolor: 'action.hover',
  '&:hover': {
    backgroundColor: Color.primaryOrangeHover,
  },
  '& svg': {
    pointerEvents: 'none',
  },
};

export const compactRichTextSx = (
  compact: boolean,
  toolbarOpen: boolean
): SxProps<Theme> => {
  if (!compact) {
    return {};
  }

  const compactContentSx = {
    boxSizing: 'border-box',
    height: `${COMPACT_FIELD_HEIGHT_PX + (toolbarOpen ? RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX : 0)}px`,
    paddingTop: toolbarOpen ? `${RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX}px` : 0,
    paddingBottom: 0,
    paddingLeft: `${RICH_TEXT_CONTENT_PADDING_PX}px`,
    paddingRight: toolbarOpen
      ? `${RICH_TEXT_CONTENT_PADDING_PX}px`
      : `${RICH_TEXT_CONTENT_PADDING_PX + FORMAT_TOGGLE_BUTTON_PX}px`,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    transition: FORMAT_TOOLBAR_LAYOUT_TRANSITION,
  } as const;

  const compactOpenContentSx = {
    ...compactContentSx,
    height: `${COMPACT_FIELD_HEIGHT_PX + RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX}px`,
    paddingTop: `${RICH_TEXT_TOOLBAR_RESERVED_HEIGHT_PX}px`,
    paddingRight: `${RICH_TEXT_CONTENT_PADDING_PX}px`,
  } as const;

  return {
    '& [data-rich-text-content="true"]': compactContentSx,
    '&[data-format-toolbar-open="true"] [data-rich-text-content="true"]':
      compactOpenContentSx,
    '& .ProseMirror': {
      minHeight: 24,
      height: 24,
      lineHeight: '24px',
      display: 'block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    '& .ProseMirror p': {
      margin: 0,
      lineHeight: '24px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .ProseMirror p.is-editor-empty:first-child::before': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
  };
};
